import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/firebase';

interface User {
  id: string;
  username: string;
  email: string;
  isActive: boolean;
  createdAt: any;
  lastLogin?: any;
  role?: string;
  licenseKey?: string;
  lastLoginIP?: string;
  lastLoginHardwareFingerprint?: string;
  lastLoginDeviceName?: string;
}

interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

interface CreateUserForm {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface EditUserForm {
  email: string;
  isActive: boolean;
}

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

export function Users() {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [resettingPasswordUser, setResettingPasswordUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [editForm, setEditForm] = useState<EditUserForm>({
    email: '',
    isActive: true
  });

  const [resetPasswordForm, setResetPasswordForm] = useState<ResetPasswordForm>({
    newPassword: '',
    confirmPassword: ''
  });

  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const getAuthHeaders = async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (currentUser) {
      try {
        const token = await currentUser.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get ID token:', error);
      }
    }
    
    return headers;
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/users?page=1&limit=50`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: UsersResponse = await response.json();

      if (data.success) {
        console.log('Received users data:', data.data); // Debug log
        
        // Log specific user data for debugging
        const svenUser = data.data.find((user: any) => user.username === 'Sven');
        if (svenUser) {
          console.log('Sven user data:', {
            username: svenUser.username,
            licenseKey: svenUser.licenseKey,
            lastLoginIP: svenUser.lastLoginIP,
            lastLoginHardwareFingerprint: svenUser.lastLoginHardwareFingerprint,
            lastLoginDeviceName: svenUser.lastLoginDeviceName
          });
        }
        
        setUsers(data.data);
      } else {
        throw new Error('Failed to fetch users');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    if (!createForm.username.trim() || !createForm.email.trim() || !createForm.password.trim()) {
      showMessage('error', 'Alle velden zijn verplicht');
      return;
    }

    if (createForm.password !== createForm.confirmPassword) {
      showMessage('error', 'Wachtwoorden komen niet overeen');
      return;
    }

    if (createForm.password.length < 6) {
      showMessage('error', 'Wachtwoord moet minimaal 6 karakters lang zijn');
      return;
    }

    setCreating(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/users`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          username: createForm.username.trim(),
          email: createForm.email.trim(),
          password: createForm.password
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowCreateModal(false);
        setCreateForm({
          username: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
        await fetchUsers();
        showMessage('success', 'Gebruiker succesvol aangemaakt');
      } else {
        throw new Error(data.error || 'Failed to create user');
      }
    } catch (err) {
      showMessage('error', 'Fout bij aanmaken van gebruiker: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setCreating(false);
    }
  };

  const updateUser = async () => {
    if (!editingUser) {
      showMessage('error', 'Geen gebruiker geselecteerd voor bewerking');
      return;
    }

    if (!editForm.email.trim()) {
      showMessage('error', 'Email is verplicht');
      return;
    }

    setUpdating(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/users/${editingUser.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          email: editForm.email.trim(),
          isActive: editForm.isActive
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowEditModal(false);
        setEditingUser(null);
        await fetchUsers();
        showMessage('success', 'Gebruiker succesvol bijgewerkt');
      } else {
        throw new Error(data.error || 'Failed to update user');
      }
    } catch (err) {
      showMessage('error', 'Fout bij bijwerken van gebruiker: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setUpdating(false);
    }
  };

  const resetPassword = async () => {
    if (!resettingPasswordUser) {
      showMessage('error', 'Geen gebruiker geselecteerd voor wachtwoord reset');
      return;
    }

    if (!resetPasswordForm.newPassword.trim()) {
      showMessage('error', 'Nieuw wachtwoord is verplicht');
      return;
    }

    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      showMessage('error', 'Wachtwoorden komen niet overeen');
      return;
    }

    if (resetPasswordForm.newPassword.length < 6) {
      showMessage('error', 'Wachtwoord moet minimaal 6 karakters lang zijn');
      return;
    }

    setResettingPassword(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/users/${resettingPasswordUser.id}/reset-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          newPassword: resetPasswordForm.newPassword
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setShowResetPasswordModal(false);
        setResettingPasswordUser(null);
        setResetPasswordForm({
          newPassword: '',
          confirmPassword: ''
        });
        showMessage('success', 'Wachtwoord succesvol gereset');
      } else {
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (err) {
      showMessage('error', 'Fout bij resetten van wachtwoord: ' + (err instanceof Error ? err.message : 'Onbekende fout'));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Weet je zeker dat je gebruiker ${user.username} wilt verwijderen?`)) {
      return;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.admin}/users/${user.id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        showMessage('success', 'Gebruiker succesvol verwijderd');
        await fetchUsers();
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      showMessage('error', 'Fout bij verwijderen van gebruiker');
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      isActive: user.isActive
    });
    setShowEditModal(true);
  };

  const openResetPasswordModal = (user: User) => {
    setResettingPasswordUser(user);
    setResetPasswordForm({
      newPassword: '',
      confirmPassword: ''
    });
    setShowResetPasswordModal(true);
  };

  const openDetailsModal = (user: User) => {
    setViewingUser(user);
    setShowDetailsModal(true);
  };

  useEffect(() => {
    fetchUsers();
  }, [currentUser]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Nooit';
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp formats
      if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.seconds !== undefined) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else {
        return 'Ongeldig';
      }
      
      if (isNaN(date.getTime())) {
        return 'Ongeldig';
      }
      
      // Use toLocaleString instead of toLocaleDateString to include time
      return date.toLocaleString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Ongeldig';
    }
  };

  const getStatusBadge = (user: User) => {
    if (user.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Actief
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Inactief
        </span>
      );
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !filters.search || 
      user.username.toLowerCase().includes(filters.search.toLowerCase()) ||
      user.email.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = !filters.status || 
      user.isActive.toString() === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Gebruikers laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-12 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
            Gebruikers Beheer
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Beheer gebruikersaccounts en toegang
          </p>
          <p className="text-sm text-gray-500">
            Overzicht van alle geregistreerde gebruikers
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
          <button
            onClick={fetchUsers}
            className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg border border-gray-200 hover:shadow-xl"
          >
            <span>🔄</span>
            <span>Vernieuwen</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            <span>👤</span>
            <span>Gebruiker Toevoegen</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden mb-8">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-16 translate-x-16 opacity-20"></div>
          <div className="relative z-10">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <span className="mr-2">🔍</span>
              Filters
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Zoeken</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Zoek op gebruikersnaam of email..."
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full border border-gray-300 rounded-xl px-3 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <option value="">Alle Status</option>
                  <option value="true">Actief</option>
                  <option value="false">Inactief</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <p className="text-sm text-gray-600">
                Toont {filteredUsers.length} van {users.length} gebruikers
              </p>
              <button
                onClick={() => setFilters({ search: '', status: '' })}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
              >
                Filters Wissen
              </button>
            </div>
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl border shadow-sm ${
            message.type === 'success' 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800' 
              : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">{message.type === 'success' ? '✅' : '❌'}</span>
                <span>{message.text}</span>
              </div>
              <button
                onClick={() => setMessage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4 mb-6 shadow-sm">
            <div className="flex">
              <span className="text-red-400">❌</span>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Fout</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Users Cards - Responsive Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredUsers.length === 0 ? (
            <div className="col-span-full">
              <div className="bg-white rounded-2xl shadow-2xl p-12 border border-gray-100 text-center">
                <span className="text-6xl mb-4 block">👥</span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Geen gebruikers gevonden</h3>
                <p className="text-gray-600">Voeg je eerste gebruiker toe of pas je filters aan</p>
              </div>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <div key={user.id} className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100 relative overflow-hidden hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full -translate-y-12 translate-x-12 opacity-20"></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {user.username}
                      </h3>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                    {getStatusBadge(user)}
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Aangemaakt</p>
                      <p className="text-sm text-gray-700">{formatDate(user.createdAt)}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Laatste Login</p>
                      <p className="text-sm text-gray-700">{formatDate(user.lastLogin)}</p>
                    </div>
                    
                    {user.licenseKey && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center">
                          <span className="mr-1">🔑</span>
                          Licentie
                        </p>
                        <p className="text-sm text-gray-700 font-mono bg-gradient-to-r from-green-50 to-emerald-50 px-2 py-1 rounded border border-green-200">
                          {user.licenseKey.substring(0, 8)}...{user.licenseKey.substring(user.licenseKey.length - 4)}
                        </p>
                      </div>
                    )}
                    
                    {user.lastLoginIP && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center">
                          <span className="mr-1">🌐</span>
                          Laatste IP Adres
                        </p>
                        <p className="text-sm text-gray-700 font-mono bg-blue-50 px-2 py-1 rounded border border-blue-200">{user.lastLoginIP}</p>
                      </div>
                    )}
                    
                    {user.lastLoginDeviceName && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center">
                          <span className="mr-1">💻</span>
                          Laatste Apparaat
                        </p>
                        <p className="text-sm text-gray-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">{user.lastLoginDeviceName}</p>
                      </div>
                    )}
                    
                    {user.lastLoginHardwareFingerprint && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center">
                          <span className="mr-1">🔒</span>
                          Hardware Fingerprint
                        </p>
                        <p 
                          className="text-xs text-gray-600 font-mono bg-orange-50 px-2 py-1 rounded border border-orange-200 break-all cursor-help"
                          title={user.lastLoginHardwareFingerprint}
                        >
                          {user.lastLoginHardwareFingerprint.substring(0, 16)}...
                        </p>
                      </div>
                    )}
                    
                    {user.role && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Rol</p>
                        <p className="text-sm text-gray-700">{user.role}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-6 flex flex-wrap gap-2">
                    <button 
                      onClick={() => openDetailsModal(user)}
                      className="w-full bg-gradient-to-r from-gray-50 to-slate-50 hover:from-gray-100 hover:to-slate-100 text-gray-700 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border border-gray-200 mb-2"
                    >
                      👁️ Alle informatie bekijken
                    </button>
                    <button 
                      onClick={() => openEditModal(user)}
                      className="flex-1 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border border-blue-200"
                    >
                      ✏️ Bewerken
                    </button>
                    <button 
                      onClick={() => openResetPasswordModal(user)}
                      className="flex-1 bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 text-yellow-700 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border border-yellow-200"
                    >
                      🔑 Reset Wachtwoord
                    </button>
                    <button 
                      onClick={() => handleDeleteUser(user)}
                      className="flex-1 bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 text-red-700 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 border border-red-200"
                    >
                      🗑️ Verwijderen
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Copyright Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          © 2025 S3e
        </p>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">👤</span>
                Nieuwe Gebruiker Toevoegen
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Gebruikersnaam *</label>
                  <input
                    type="text"
                    value={createForm.username}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, username: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer gebruikersnaam in"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer email adres in"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Wachtwoord *</label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer wachtwoord in (minimaal 6 karakters)"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Bevestig Wachtwoord *</label>
                  <input
                    type="password"
                    value={createForm.confirmPassword}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Bevestig wachtwoord"
                    required
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={createUser}
                  disabled={creating}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl"
                >
                  {creating ? 'Toevoegen...' : 'Gebruiker Toevoegen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">✏️</span>
                Gebruiker Bewerken
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Bewerk gegevens voor: <strong>{editingUser.username}</strong>
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Voer email adres in"
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="ml-2 block text-sm font-semibold text-gray-900">
                    Gebruiker is actief
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUser(null);
                  }}
                  disabled={updating}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={updateUser}
                  disabled={updating}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl"
                >
                  {updating ? 'Bijwerken...' : 'Gebruiker Bijwerken'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resettingPasswordUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🔑</span>
                Wachtwoord Resetten
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Reset wachtwoord voor: <strong>{resettingPasswordUser.username}</strong>
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Nieuw Wachtwoord *</label>
                  <input
                    type="password"
                    value={resetPasswordForm.newPassword}
                    onChange={(e) => setResetPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Voer nieuw wachtwoord in (minimaal 6 karakters)"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Bevestig Nieuw Wachtwoord *</label>
                  <input
                    type="password"
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) => setResetPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Bevestig nieuw wachtwoord"
                    required
                  />
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mt-4">
                <div className="flex items-start">
                  <span className="text-yellow-400 mr-2">⚠️</span>
                  <div className="text-sm text-yellow-800">
                    <p className="font-semibold">Let op:</p>
                    <p>Het wachtwoord wordt onmiddellijk gewijzigd. De gebruiker moet inloggen met het nieuwe wachtwoord.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setResettingPasswordUser(null);
                    setResetPasswordForm({
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  disabled={resettingPassword}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors duration-200"
                >
                  Annuleren
                </button>
                <button
                  onClick={resetPassword}
                  disabled={resettingPassword}
                  className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 flex items-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <span>🔑</span>
                  <span>{resettingPassword ? 'Resetten...' : 'Wachtwoord Resetten'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && viewingUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-2xl bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <span className="mr-3">👤</span>
                  Gebruiker Details - {viewingUser.username}
                </h3>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setViewingUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basis Informatie */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <h4 className="text-lg font-bold text-blue-900 mb-4 flex items-center">
                    <span className="mr-2">📋</span>
                    Basis Informatie
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-blue-700">Gebruikersnaam</p>
                      <p className="text-gray-800 font-mono bg-white px-3 py-2 rounded border">{viewingUser.username}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-blue-700">Email</p>
                      <p className="text-gray-800 bg-white px-3 py-2 rounded border">{viewingUser.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-blue-700">Status</p>
                      <div className="bg-white px-3 py-2 rounded border">
                        {getStatusBadge(viewingUser)}
                      </div>
                    </div>
                    {viewingUser.role && (
                      <div>
                        <p className="text-sm font-semibold text-blue-700">Rol</p>
                        <p className="text-gray-800 bg-white px-3 py-2 rounded border">{viewingUser.role}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Licentie Informatie */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                  <h4 className="text-lg font-bold text-green-900 mb-4 flex items-center">
                    <span className="mr-2">🔑</span>
                    Licentie Informatie
                  </h4>
                  <div className="space-y-3">
                    {viewingUser.licenseKey ? (
                      <div>
                        <p className="text-sm font-semibold text-green-700">Licentie Key</p>
                        <p className="text-gray-800 font-mono bg-white px-3 py-2 rounded border break-all text-sm">
                          {viewingUser.licenseKey}
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <span className="text-4xl mb-2 block">🚫</span>
                        <p className="text-gray-600">Geen licentie toegewezen</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Datum Informatie */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                  <h4 className="text-lg font-bold text-purple-900 mb-4 flex items-center">
                    <span className="mr-2">📅</span>
                    Datum Informatie
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-semibold text-purple-700">Aangemaakt</p>
                      <p className="text-gray-800 bg-white px-3 py-2 rounded border">{formatDate(viewingUser.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-purple-700">Laatste Login</p>
                      <p className="text-gray-800 bg-white px-3 py-2 rounded border">{formatDate(viewingUser.lastLogin)}</p>
                    </div>
                  </div>
                </div>

                {/* Login Details */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
                  <h4 className="text-lg font-bold text-orange-900 mb-4 flex items-center">
                    <span className="mr-2">🔍</span>
                    Laatste Login Details
                  </h4>
                  <div className="space-y-3">
                    {viewingUser.lastLoginIP ? (
                      <>
                        <div>
                          <p className="text-sm font-semibold text-orange-700">IP Adres</p>
                          <p className="text-gray-800 font-mono bg-white px-3 py-2 rounded border">{viewingUser.lastLoginIP}</p>
                        </div>
                        {viewingUser.lastLoginDeviceName && (
                          <div>
                            <p className="text-sm font-semibold text-orange-700">Apparaat Naam</p>
                            <p className="text-gray-800 bg-white px-3 py-2 rounded border">{viewingUser.lastLoginDeviceName}</p>
                          </div>
                        )}
                        {viewingUser.lastLoginHardwareFingerprint && (
                          <div>
                            <p className="text-sm font-semibold text-orange-700">Hardware Fingerprint</p>
                            <p className="text-gray-800 font-mono bg-white px-3 py-2 rounded border break-all text-xs">
                              {viewingUser.lastLoginHardwareFingerprint}
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <span className="text-4xl mb-2 block">❓</span>
                        <p className="text-gray-600">Geen login details beschikbaar</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setViewingUser(null);
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-xl font-semibold transition-all duration-200"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users; 